/**
 * The relationship-sphere taxonomy (SOCIAL_DOMAIN_SPEC §4, SO3). Bonds are
 * grouped into 4 spheres, each with subtypes. Romantic relationships live as
 * subtypes under Familial (Romantic Partner / Ex-Partner) — no separate sphere.
 *
 * Migrated from the old 4 categories (Family / Romantic / Professional / Social)
 * by `..._social_sphere_taxonomy.sql`, which also swaps the
 * `valid_relationship_category` CHECK and the `respond_network_request` default.
 */
export type RelationshipCategory = 'Professional' | 'Communal' | 'Platonic' | 'Familial';

export interface RelationshipType {
  name: string;
  category: RelationshipCategory;
  icon: string;
}

export const RELATIONSHIP_TYPES: RelationshipType[] = [
  // Professional — the Work Sphere
  { name: 'Partners / Co-founders', category: 'Professional', icon: 'Handshake' },
  { name: 'Superiors / Managers', category: 'Professional', icon: 'Crown' },
  { name: 'Subordinates / Employees', category: 'Professional', icon: 'UserCheck' },
  { name: 'Colleagues / Peers', category: 'Professional', icon: 'Briefcase' },
  { name: 'Mentors / Mentees', category: 'Professional', icon: 'GraduationCap' },
  { name: 'Service Providers / Domestic Staff', category: 'Professional', icon: 'Wrench' },
  { name: 'Clients / Customers', category: 'Professional', icon: 'Building' },

  // Communal — the Shared Sphere
  { name: 'Neighbors', category: 'Communal', icon: 'Home' },
  { name: 'Co-Affiliates / Members', category: 'Communal', icon: 'Users' },
  { name: 'Alumni', category: 'Communal', icon: 'GraduationCap' },
  { name: 'Citizens / Townspeople', category: 'Communal', icon: 'Landmark' },
  { name: 'Digital Community Members', category: 'Communal', icon: 'Globe' },

  // Platonic — the Social Sphere
  { name: 'Confidants / Inner Circle', category: 'Platonic', icon: 'Star' },
  { name: 'Best Friends / Besties', category: 'Platonic', icon: 'Smile' },
  { name: 'Activity Partners / Companions', category: 'Platonic', icon: 'Users' },
  { name: 'Casual Friends', category: 'Platonic', icon: 'User' },
  { name: 'Acquaintances', category: 'Platonic', icon: 'UserPlus' },

  // Familial — the Kinship Sphere (Romantic lives here as subtypes)
  { name: 'Immediate Family', category: 'Familial', icon: 'Home' },
  { name: 'Extended Family', category: 'Familial', icon: 'Users' },
  { name: 'In-Laws', category: 'Familial', icon: 'Users' },
  { name: 'Chosen Family', category: 'Familial', icon: 'HeartHandshake' },
  { name: 'Romantic Partner', category: 'Familial', icon: 'Heart' },
  { name: 'Ex-Partner', category: 'Familial', icon: 'HeartCrack' },
];

export const RELATIONSHIP_CATEGORIES: RelationshipCategory[] = [
  'Professional',
  'Communal',
  'Platonic',
  'Familial',
];

export const CATEGORY_COLORS: Record<RelationshipCategory, string> = {
  Professional: '#3B82F6',
  Communal: '#14B8A6',
  Platonic: '#10B981',
  Familial: '#EC4899',
};

/** Lucide icon NAME per sphere (resolved to a component at each call site). */
export const CATEGORY_ICONS: Record<RelationshipCategory, string> = {
  Professional: 'Briefcase',
  Communal: 'Building2',
  Platonic: 'Users',
  Familial: 'Home',
};

export function getRelationshipTypesByCategory(category: RelationshipCategory): RelationshipType[] {
  return RELATIONSHIP_TYPES.filter(rt => rt.category === category);
}

export function getRelationshipType(name: string): RelationshipType | undefined {
  return RELATIONSHIP_TYPES.find(rt => rt.name === name);
}
